import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import warehouseBinServices from "app/services/warehouse-bin-services";
import WarehouseHelpDialog from "./WarehouseHelpDialog";

const INITIAL = {
    zone: "A",
    aisle: "1",
    racks: 10,
    levels: 4,
    positions: 1,
    kind: "picking",
    layout_x: 0,
    layout_y: 0,
    orientation: "horizontal",
};

const KINDS = [
    { value: "picking", label: "Picking" },
    { value: "reserva", label: "Reserva" },
    { value: "recepcion", label: "Recepción" },
    { value: "despacho", label: "Despacho" },
    { value: "staging", label: "Staging" },
    { value: "devoluciones", label: "Devoluciones" },
];

// Nadie va a dibujar 400 huecos a mano ni tipearles el código. Un pasillo típico
// se describe con cuatro números.
const GenerateAisleModal = ({ open, onClose, centreId, onGenerated }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [form, setForm] = useState(INITIAL);
    const [saving, setSaving] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    const handleChange = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const total = useMemo(() => {
        const racks = Number(form.racks) || 0;
        const levels = Number(form.levels) || 0;
        const positions = Number(form.positions) || 0;
        return racks * levels * positions;
    }, [form.racks, form.levels, form.positions]);

    // Muestra cómo va a quedar el código antes de generar nada: es la forma más
    // rápida de darse cuenta de que la convención elegida no era la que se quería.
    const sample = useMemo(() => {
        const pad = (value) => String(value).padStart(2, "0");
        const parts = [];
        if (form.zone) parts.push(String(form.zone).toUpperCase());
        if (form.aisle) parts.push(pad(form.aisle));
        parts.push(pad(1));
        if (Number(form.levels) > 1 || Number(form.positions) > 1) parts.push("A");
        if (Number(form.positions) > 1) parts.push(pad(1));
        return parts.join("-");
    }, [form.zone, form.aisle, form.levels, form.positions]);

    const submit = async () => {
        try {
            setSaving(true);
            const result = await warehouseBinServices.generateAisle({
                centre_id: centreId,
                ...form,
            });
            enqueueSnackbar(result.message || "Pasillo generado", { variant: "success" });
            onGenerated();
            onClose();
            setForm(INITIAL);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo generar el pasillo", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>Generar un pasillo</span>
                    <Tooltip title="Qué significa cada campo, con ejemplos">
                        <IconButton size="small" onClick={() => setHelpOpen(true)}>
                            <HelpOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Zona"
                            value={form.zone}
                            onChange={handleChange("zone")}
                            helperText="Opcional"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Pasillo"
                            value={form.aisle}
                            onChange={handleChange("aisle")}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Racks"
                            value={form.racks}
                            onChange={handleChange("racks")}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Niveles"
                            value={form.levels}
                            onChange={handleChange("levels")}
                            helperText="A = piso"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Posiciones por nivel"
                            value={form.positions}
                            onChange={handleChange("positions")}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Tipo"
                            value={form.kind}
                            onChange={handleChange("kind")}
                        >
                            {KINDS.map((kind) => (
                                <MenuItem key={kind.value} value={kind.value}>
                                    {kind.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Orientación"
                            value={form.orientation}
                            onChange={handleChange("orientation")}
                        >
                            <MenuItem value="horizontal">Horizontal</MenuItem>
                            <MenuItem value="vertical">Vertical</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Col."
                                value={form.layout_x}
                                onChange={handleChange("layout_x")}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Fila"
                                value={form.layout_y}
                                onChange={handleChange("layout_y")}
                            />
                        </Stack>
                    </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                        Se van a crear <strong>{total}</strong> posiciones. La primera quedará como{" "}
                        <strong>{sample}</strong>.
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Volver a generar el mismo pasillo no duplica nada: actualiza lo que ya existe y agrega lo que
                        falte.
                    </Typography>
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton variant="contained" loading={saving} onClick={submit} disabled={total < 1}>
                    Generar
                </LoadingButton>
            </DialogActions>

            <WarehouseHelpDialog
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                defaultSection="campos"
            />
        </Dialog>
    );
};

export default GenerateAisleModal;
